import * as React from 'react';
import { useMediaQuery, useTheme } from '@mui/system';
import { useMatches } from 'react-router';

export type SetPageTitleOptions = {
    appendAppName?: boolean;
};

export type PageRouteHandle = {
    title?: string;
};

export type FullLayoutContextType = {
    isSidebarOpen: boolean;
    jobName?: string;
    setJobName: React.Dispatch<React.SetStateAction<string>>;
    setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isCustomizerDrawerOpen: boolean;
    setCustomizerDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isProfileHeaderVisible: boolean;
    setProfileHeaderVisible: React.Dispatch<React.SetStateAction<boolean>>;
    toggleCustomizerDrawer: () => void;
    pageTitle: string;
    setPageTitle: (title: string, options?: SetPageTitleOptions) => void;
    clearPageTitle: () => void;
    isPrescriberSwitchDisabled: boolean;
    setPrescriberSwitchDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    prescriberDisplayOverride: number | undefined;
    setPrescriberDisplayOverride: React.Dispatch<React.SetStateAction<number | undefined>>;
};

export const FullLayoutContext = React.createContext<FullLayoutContextType>({
    isSidebarOpen: true,
    setSidebarOpen: () => {},
    isCustomizerDrawerOpen: false,
    setCustomizerDrawerOpen: () => {},
    isProfileHeaderVisible: true,
    setProfileHeaderVisible: () => {},
    toggleCustomizerDrawer: () => {},
    setJobName: () => {},
    pageTitle: '',
    setPageTitle: () => {},
    clearPageTitle: () => {},
    isPrescriberSwitchDisabled: false,
    setPrescriberSwitchDisabled: () => {},
    prescriberDisplayOverride: undefined,
    setPrescriberDisplayOverride: () => {},
});

export type FullLayoutProviderProps = {
    children: React.ReactNode;
    defaultPageTitle?: string;
};

export const FullLayoutProvider: React.FunctionComponent<FullLayoutProviderProps> = function ({
    children,
    defaultPageTitle = 'Back2Digital',
}) {
    const theme = useTheme();
    const matches = useMatches();

    const smBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));

    const [isSidebarOpen, setSidebarOpen] = React.useState(smBreakpoint);
    const [jobName, setJobName] = React.useState<string | undefined>(undefined);
    const [isCustomizerDrawerOpen, setCustomizerDrawerOpen] = React.useState(false);
    const [isProfileHeaderVisible, setProfileHeaderVisible] = React.useState(true);
    const [explicitPageTitle, setExplicitPageTitle] = React.useState<string | undefined>(undefined);
    const [isPrescriberSwitchDisabled, setPrescriberSwitchDisabled] = React.useState(false);
    const [prescriberDisplayOverride, setPrescriberDisplayOverride] = React.useState<
        number | undefined
    >(undefined);

    const toggleCustomizerDrawer = React.useCallback(() => {
        setCustomizerDrawerOpen(!isCustomizerDrawerOpen);
    }, [isCustomizerDrawerOpen]);

    const setPageTitle = React.useCallback(
        (title: string, options?: SetPageTitleOptions) => {
            const { appendAppName = true } = options ?? {};
            const finalTitle =
                appendAppName && defaultPageTitle ? `${title} - ${defaultPageTitle}` : title;
            setExplicitPageTitle(finalTitle);
        },
        [defaultPageTitle],
    );

    const clearPageTitle = React.useCallback(() => {
        setExplicitPageTitle(undefined);
    }, []);

    const computedPageTitle = React.useMemo(() => {
        if (explicitPageTitle) {
            return explicitPageTitle;
        }

        const routeTitle = matches
            .map((match) => (match.handle as PageRouteHandle)?.title)
            .filter(Boolean)
            .pop();

        if (routeTitle) {
            return `${routeTitle} - ${defaultPageTitle}`;
        }

        return defaultPageTitle;
    }, [explicitPageTitle, matches, defaultPageTitle]);

    const providerValue = React.useMemo(
        () => ({
            jobName,
            setJobName,
            isSidebarOpen,
            setSidebarOpen,
            isCustomizerDrawerOpen,
            setCustomizerDrawerOpen,
            toggleCustomizerDrawer,
            isProfileHeaderVisible,
            setProfileHeaderVisible,
            pageTitle: computedPageTitle,
            setPageTitle,
            clearPageTitle,
            isPrescriberSwitchDisabled,
            setPrescriberSwitchDisabled,
            prescriberDisplayOverride,
            setPrescriberDisplayOverride,
        }),
        [
            isCustomizerDrawerOpen,
            isProfileHeaderVisible,
            isPrescriberSwitchDisabled,
            isSidebarOpen,
            jobName,
            prescriberDisplayOverride,
            toggleCustomizerDrawer,
            computedPageTitle,
            setPageTitle,
            clearPageTitle,
        ],
    );

    return (
        <FullLayoutContext.Provider value={providerValue}>{children}</FullLayoutContext.Provider>
    );
};

export const useFullLayout = () => React.useContext(FullLayoutContext);
